import { FileText, Map, Scale, Shield } from "lucide-react";
import { useTranslation } from "react-i18next";
import LandingPageTemplate, { type LandingPageConfig } from "@/components/landing/LandingPageTemplate";
import { buildFaqSchema } from "@/components/landing/faqSchema";

export default function Urbanismo() {
  const { t } = useTranslation();

  const faqItems = [
    { qKey: "landing.urbanismo.faq_q1", aKey: "landing.urbanismo.faq_a1" },
    { qKey: "landing.urbanismo.faq_q2", aKey: "landing.urbanismo.faq_a2" },
    { qKey: "landing.urbanismo.faq_q3", aKey: "landing.urbanismo.faq_a3" },
    { qKey: "landing.urbanismo.faq_q4", aKey: "landing.urbanismo.faq_a4" },
    { qKey: "landing.urbanismo.faq_q5", aKey: "landing.urbanismo.faq_a5" },
    { qKey: "landing.urbanismo.faq_q6", aKey: "landing.urbanismo.faq_a6" },
    { qKey: "landing.urbanismo.faq_q7", aKey: "landing.urbanismo.faq_a7" },
    { qKey: "landing.urbanismo.faq_q8", aKey: "landing.urbanismo.faq_a8" },
    { qKey: "landing.urbanismo.faq_q9", aKey: "landing.urbanismo.faq_a9" },
  ];

  const faqSchema = buildFaqSchema(
    faqItems.map(item => ({ q: t(item.qKey), a: t(item.aKey) })),
  );

  const urbanismoSchema = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Babete Urbanismo",
    "url": "https://urbanismo.babete.pt",
    "applicationCategory": "LegalService",
    "operatingSystem": "Web",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "EUR" },
    "description": t("landing.urbanismo.seo_description"),
    "inLanguage": "pt",
    "creator": { "@type": "Organization", "name": "Babete", "url": "https://www.babete.pt" },
    "about": [
      { "@type": "Legislation", "name": "RGEU — Regulamento Geral das Edificações Urbanas", "legislationIdentifier": "Decreto-Lei n.º 38 382/1951", "url": "https://diariodarepublica.pt/dr/legislacao-consolidada/decreto-lei/1951-120610500" },
      { "@type": "Legislation", "name": "RJUE — Regime Jurídico da Urbanização e Edificação", "legislationIdentifier": "Decreto-Lei n.º 555/99", "url": "https://diariodarepublica.pt/dr/legislacao-consolidada/decreto-lei/1999-34567875" },
      { "@type": "Legislation", "name": "SIMPLEX — Simplificação Urbanística", "legislationIdentifier": "Decreto-Lei n.º 10/2024", "url": "https://diariodarepublica.pt/dr/detalhe/decreto-lei/10-2024-836222484" },
      { "@type": "Legislation", "name": "RJIGT — Regime Jurídico dos Instrumentos de Gestão Territorial", "legislationIdentifier": "Decreto-Lei n.º 80/2015", "url": "https://diariodarepublica.pt/dr/legislacao-consolidada/decreto-lei/2015-105297982" },
      { "@type": "Legislation", "name": "RJREN — Regime Jurídico da Reserva Ecológica Nacional", "url": "https://diariodarepublica.pt/dr/legislacao-consolidada/decreto-lei/2008-34501775" },
      { "@type": "Legislation", "name": "RJRU — Regime Jurídico da Reabilitação Urbana", "url": "https://diariodarepublica.pt/dr/legislacao-consolidada/decreto-lei/2009-34511675" },
      { "@type": "Legislation", "name": "SIR — Sistema da Indústria Responsável", "url": "https://diariodarepublica.pt/dr/legislacao-consolidada/decreto-lei/2012-67179556" },
      { "@type": "Legislation", "name": "RCCTE — Requisitos de Desempenho Energético", "legislationIdentifier": "Decreto-Lei n.º 101-D/2020", "url": "https://diariodarepublica.pt/dr/detalhe/decreto-lei/101-d-2020-150570704" },
    ],
    "citation": [
      { "@type": "LegislationObject", "name": "Diário da República", "url": "https://diariodarepublica.pt" },
    ],
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      { "@type": "ListItem", "position": 1, "name": "Babete", "item": "https://www.babete.pt/" },
      { "@type": "ListItem", "position": 2, "name": t("landing.urbanismo.seo_title"), "item": "https://www.babete.pt/urbanismo" },
    ],
  };

  const config: LandingPageConfig = {
    moduleNumber: "01",
    moduleKey: "landing.urbanismo.module",
    titleLine1Key: "landing.urbanismo.title1",
    titleLine2Key: "landing.urbanismo.title2",
    heroDescKey: "landing.urbanismo.hero_desc",
    ctaUrl: "https://urbanismo.babete.pt/",
    ctaLabelKey: "landing.urbanismo.cta",
    narrativeLabelKey: "landing.urbanismo.narrative_label",
    narrativeTitleHtml: (t) => (
      <>
        {t("landing.urbanismo.narrative_title_1")}
        <br />
        {t("landing.urbanismo.narrative_title_2")}
        <br />
        <span style={{ opacity: 0.4 }}>{t("landing.urbanismo.narrative_title_3")}</span>
      </>
    ),
    narrativeP1Key: "landing.urbanismo.narrative_p1",
    narrativeP2Key: "landing.urbanismo.narrative_p2",
    features: [
      {
        icon: <FileText className="w-8 h-8 mb-6" style={{ color: "var(--theme-accent, #FF3300)" }} />,
        titleKey: "landing.urbanismo.f1_title",
        descKey: "landing.urbanismo.f1_desc",
      },
      {
        icon: <Map className="w-8 h-8 mb-6" style={{ color: "var(--theme-accent, #FF3300)" }} />,
        titleKey: "landing.urbanismo.f2_title",
        descKey: "landing.urbanismo.f2_desc",
      },
      {
        icon: <Scale className="w-8 h-8 mb-6" style={{ color: "var(--theme-accent, #FF3300)" }} />,
        titleKey: "landing.urbanismo.f3_title",
        descKey: "landing.urbanismo.f3_desc",
      },
      {
        icon: <Shield className="w-8 h-8 mb-6" style={{ color: "var(--theme-accent, #FF3300)" }} />,
        titleKey: "landing.shared.f_protection_title",
        descKey: "landing.shared.f_protection_desc",
      },
    ],
    solutionCtaLabelKey: "landing.urbanismo.solution_cta",
    faq: faqItems,
    seo: {
      titleKey: "landing.urbanismo.seo_title",
      descriptionKey: "landing.urbanismo.seo_description",
      canonical: "https://www.babete.pt/urbanismo",
      schema: [urbanismoSchema, faqSchema, breadcrumbSchema],
    },
  };

  return <LandingPageTemplate config={config} />;
}
